import {BrowserClient, breadcrumbsIntegration, dedupeIntegration, defaultStackParser, eventFiltersIntegration, getCurrentScope, globalHandlersIntegration, httpContextIntegration, makeFetchTransport, linkedErrorsIntegration, browserTracingIntegration} from "@sentry/browser";
import { DENY_URLS, dropThirdPartyNoise, IGNORE_ERRORS } from "./sentry-noise.ts";
import { release } from "./release.ts";

/**
 * Exported for `src/sentry.test.ts` only. Nothing else should reach for the
 * client — every entry point goes through {@link initSentry}, which is the
 * whole point of that function (decision 037).
 */
export const sentryClient = new BrowserClient({
  dsn: "https://56ce99ce80994bab79dab62d06078c97@o4509634382331904.ingest.us.sentry.io/4509634387509248",
  environment: import.meta.env.MODE,
  transport: makeFetchTransport,
  stackParser: defaultStackParser,
  tracesSampleRate: 1.0,
  // No `dataCollection`: Sentry 11's default is what `sendDefaultPii: true` gave
  // us in 10. Two of its categories reach what we install: `userInfo` (Sentry
  // infers the sender's IP) and `urlQueryParams` (tracing records the query
  // strings of the requests it times, minus Sentry's sensitive keys).
  // `httpContextIntegration` below adds the page's address, the referring page
  // and the user agent, which the about page lists. The browser SDK collects no
  // bodies or cookies through the integrations below.
  // What we decline to send, and why: src/sentry-noise.ts (decision 046). The
  // rules live there as pure, tested predicates rather than inline literals,
  // because each one is a claim about a specific third party that has to stay
  // checkable — and because "does this match a Supabase network failure?" is a
  // question worth being able to ask in a test.
  denyUrls: [...DENY_URLS],
  ignoreErrors: [...IGNORE_ERRORS],
  beforeSend: dropThirdPartyNoise,
  integrations: [
    browserTracingIntegration(),
    breadcrumbsIntegration(),
    // This is what makes `denyUrls` and `ignoreErrors` above do anything. They
    // are not client-level behaviour — they are implemented BY this integration,
    // which ships in Sentry's defaults and is therefore absent here, because
    // passing an explicit `integrations` array replaces the defaults rather than
    // extending them. Without it the two options are accepted, type-checked and
    // silently inert.
    eventFiltersIntegration(),
    // No feedbackIntegration. Sentry hears about failures; it is no longer how
    // a person tells us about one — that is <feedback-form>, posting to our own
    // write API (decisions 039, 065). Sentry's widget could only deliver a report if
    // the browser could reach sentry.io, and a content blocker or a captive
    // portal is enough that it cannot; on 2026-09-08 a report naming three real
    // bugs died in the form because of it.
    globalHandlersIntegration(),
    // Also a default, so also lost to the explicit array until salish-flzd. It
    // puts the page's address and the browser's user agent on each event.
    // Without the user agent, Sentry can't name the browser, its inbound
    // crawler filter has nothing to match, and neither has `isCrawler` in
    // `beforeSend`: Meta's crawler reached Sentry as 68 visitors (SALISHSEA-IO-4B).
    httpContextIntegration(),
    linkedErrorsIntegration(),
    dedupeIntegration(),
  ],
});

/**
 * Bind the client and install its integrations — the one call every entry point
 * makes, so the four of them cannot disagree about Sentry.
 *
 * Nothing is transmitted outside production, and the gate is on the *binding*
 * rather than on `init()`: binding the client to the scope is what makes
 * `captureException` send, while `init()` only installs integrations. Reasoning
 * and the rejected alternatives are in
 * [decision 037](../docs/decisions/037-sentry-transmits-from-production-only.md).
 */
export function initSentry(): void {
  if (!import.meta.env.PROD) return;
  getCurrentScope().setClient(sentryClient);
  // The release comes from release.json, not the bundle (src/release.ts), so it
  // arrives after the client is made; an event waits for it.
  getCurrentScope().addEventProcessor(async event => {
    if (event.release) return event;
    const builtFrom = await release();
    return builtFrom === 'unknown' ? event : {...event, release: builtFrom};
  });
  sentryClient.init();
}
